import { kindText } from '../../Fianza/copy'
import { useTranslation } from 'react-i18next'
import { Card, FlowLayout, Icon, Skeleton, StepList } from '@/ds'
import type { Step } from '@/ds'
import { formatMoneyShort, locale, time } from '@/lib/format'
import { readQuery, useAppNavigate } from '@/lib/query-params'
import { changeQuery } from '../../Reserva/shared'
import { useAsync } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import { checkinService } from '@/services/checkin'
import { fianzaService } from '@/services/fianza'
import { holderTraveler, useCheckinFlow } from '../CheckinFlow'
import { stepPath } from '../flow'
import { useApiLang } from '../useSave'

const DEPOSIT_ROUTE = '/reserva/fianza/reservar'

/** Termina en la app de iOS (app actual: CheckinCompleteForm, D26). */
function closeOnIos(): boolean {
  if (readQuery().device !== 'ios') return false
  window.close()
  return true
}

/**
 * Paso 8 · Tu registro está hecho (CheckIn.dc.html step=8). "Qué viene ahora" reutiliza la lista de pasos:
 * personas pendientes, fianza (real, E2) y llave (derivada de E1, D6). Solo lectura de fianza y llave:
 * el flujo de pago es del bloque 3.
 */
export function Listo() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const { go } = useAppNavigate()
  const { stay, draft } = useCheckinFlow()

  const key = `${stay.guestCode}|${stay.registration.pending}`
  const [occupants] = useAsync(`listo-occ:${key}`, (signal) => checkinService.getOccupants(stay, signal))
  const [deposit] = useAsync(`listo-dep:${key}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [keyStatus] = useAsync(`listo-key:${key}`, (signal) => accesosService.getKey(stay, signal))

  const email = draft.guest.email || holderTraveler(stay)?.details.email || ''
  const loading = occupants.kind === 'loading' || deposit.kind === 'loading' || keyStatus.kind === 'loading'
  const pendingPeople = occupants.kind === 'ready' ? occupants.value.filter((o) => o.state === 'pending' || o.state === 'invited') : []
  const dep = deposit.kind === 'ready' ? deposit.value : null
  const depositPending = dep?.status === 'pending'
  const longDay = new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(stay.dates.checkIn)).replace(',', '')
  const whenKey = t('v2.estancia.cabecera.aLas', { day: longDay, time: time(stay.dates.checkIn, lang) })

  const steps: Step[] = [
    ...pendingPeople.map(
      (o, i): Step => ({
        id: `p-${o.id}`,
        state: 'progress',
        title: t('v2.checkin.listo.terminaRegistro', { name: o.name ?? t('v2.checkin.ocupantes.persona', { n: stay.registration.travelers.length + i + 1 }) }),
        subtitle: t('v2.checkin.listo.terminaTexto'),
      }),
    ),
    ...(dep && depositPending
      ? [{ id: 'deposit', state: 'pending' as const, title: t('v2.checkin.listo.fianzaTitulo'), subtitle: kindText(dep.kind, 'listo', t, { amount: formatMoneyShort(dep.amount, lang) }) ?? t('v2.checkin.listo.fianzaTexto', { amount: formatMoneyShort(dep.amount, lang) }) }]
      : []),
    ...(keyStatus.kind === 'ready' && keyStatus.value.state === 'locked' ? [{ id: 'key', state: 'locked' as const, title: t('v2.checkin.listo.llaveTitulo'), subtitle: t('v2.checkin.listo.llaveTexto', { when: whenKey.charAt(0).toUpperCase() + whenKey.slice(1) }) }] : []),
  ]

  const toStay = () => closeOnIos() || go('/')
  const primary = depositPending
    ? { label: t('v2.checkin.listo.reservarFianza'), onClick: () => go(DEPOSIT_ROUTE, { query: changeQuery('estancia') }) }
    : pendingPeople.some((o) => o.state === 'pending')
      ? { label: t('v2.legacy.stepper.checkinCompleteForm.goBackButton'), onClick: () => go(stepPath('1')) }
      : { label: t('v2.checkin.listo.volver'), onClick: toStay }

  return (
    <FlowLayout
      hero={<Icon name="check-circle" size={64} />}
      title={t('v2.checkin.listo.titulo')}
      intro={email && checkinService.notices.contractByEmail ? t('v2.checkin.listo.copia', { email }) : undefined}
      primary={loading ? undefined : primary}
      secondary={loading || primary.onClick === toStay ? undefined : { label: t('v2.checkin.listo.volver'), onClick: toStay }}
    >
      {(loading || steps.length > 0) && <h2 className="checkin__h2">{t('v2.checkin.listo.queViene')}</h2>}
      {loading ? (
        <div aria-busy="true" className="checkin__stack">
          <Skeleton height={64} radius={16} />
          <Skeleton height={64} radius={16} />
        </div>
      ) : (
        steps.length > 0 && (
          <Card flush>
            <StepList steps={steps} collapseWhenDone={false} onSelect={(s) => (s.id === 'deposit' ? go(DEPOSIT_ROUTE, { query: changeQuery('estancia') }) : go(stepPath('1')))} />
          </Card>
        )
      )}
    </FlowLayout>
  )
}
